/**
 * Header Position JavaScript
 *
 * Handles promo banner dismissal functionality with:
 * - localStorage persistence for banner dismissed state
 * - Cookie fallback for server-side detection
 * - Cleanup function for dynamic sites (SPA, AJAX navigation)
 *
 * @since 1.0.0
 * @since 1.20.0 Added cleanup mechanism and global destroy function.
 */
( function () {
	'use strict';

	// Cookie expiration: 1 year in seconds (365 days × 24 hours × 60 minutes × 60 seconds)
	const COOKIE_MAX_AGE_ONE_YEAR = 31536000;

	// Module state for cleanup
	const clickHandlers = [];

	/**
	 * Initialize promo banner dismissal functionality.
	 *
	 * Handles:
	 * - Checking localStorage for previously dismissed state
	 * - Applying dismissed state to prevent banner flash
	 * - Setting up click handlers for dismiss buttons
	 * - Persisting dismissal state to localStorage and cookies
	 *
	 * @since 1.0.0
	 */
	function initPromoBanner() {
		// Clean up any existing handlers first to prevent duplicates on re-init
		destroyPromoBanner();

		const promoBannerContainer = document.getElementById( 'promo-banner' );

		// Apply banner state immediately to prevent flashing
		let bannerDismissed = false;
		try {
			bannerDismissed =
				localStorage.getItem( 'devwp_promo_banner_dismissed' ) ===
				'true';
		} catch ( e ) {
			// Storage unavailable (restricted browser/disabled).
		}

		if ( bannerDismissed ) {
			// Apply dismissed state immediately
			document.body.classList.add( 'banner-dismissed' );
			document.body.classList.remove( 'has-promo-banner' );
		} else if ( promoBannerContainer ) {
			// Only add has-promo-banner class if banner is not dismissed
			document.body.classList.add( 'has-promo-banner' );
		}

		// Handle promo banner dismissal
		const closeButtons = document.querySelectorAll(
			'#promo-banner .btn-close'
		);
		closeButtons.forEach( function ( button ) {
			const clickHandler = function () {
				// Update classes to trigger the appropriate CSS rules
				document.body.classList.remove( 'has-promo-banner' );
				document.body.classList.add( 'banner-dismissed' );

				// Save state in multiple ways for reliability
				try {
					localStorage.setItem(
						'devwp_promo_banner_dismissed',
						'true'
					);
				} catch ( e ) {
					// Storage unavailable (restricted browser/disabled).
				}

				// Also set a cookie for server-side detection
				document.cookie = `devwp_banner_dismissed=true; path=/; max-age=${ COOKIE_MAX_AGE_ONE_YEAR }; SameSite=Lax`;

				// Set HTML class for immediate CSS application
				document.documentElement.classList.add(
					'banner-state-dismissed'
				);

				// Announce dismissal to screen readers
				const announcement = document.getElementById(
					'devwp-sr-announcement'
				);
				if ( announcement ) {
					announcement.textContent = 'Promotional banner dismissed';
				}
			};
			button.addEventListener( 'click', clickHandler );
			// Store handler for cleanup
			clickHandlers.push( { button, handler: clickHandler } );
		} );
	}

	/**
	 * Cleanup function for dynamic sites (SPA, AJAX navigation)
	 *
	 * Call this before re-initializing or when component is unmounted.
	 * Prevents memory leaks by removing event listeners.
	 *
	 * @since 1.20.0
	 */
	function destroyPromoBanner() {
		clickHandlers.forEach( function ( item ) {
			if ( item.button && item.handler ) {
				item.button.removeEventListener( 'click', item.handler );
			}
		} );
		clickHandlers.length = 0;
	}

	// Expose init and destroy functions globally for dynamic sites
	window.devwpPromoBanner = {
		init: initPromoBanner,
		destroy: destroyPromoBanner,
	};

	document.addEventListener( 'DOMContentLoaded', initPromoBanner );
} )();
